import {
	ContentCardRoot,
	ContentCardImageWrapper,
	ContentCardImageIcon,
	ContentCardBody,
	ContentCardText,
	ContentCardTitle,
	ContentCardSubtitle,
	ContentCardDescription,
	ContentCardAction,
	ContentCardIcon,
	ContentCardButtonRow,
	ContentCardLogo,
	ContentCardTitleRow,
	ContentCardLink,
} from './ContentCard.styled';
import React from 'react';
import { Icon, IconName } from '../icon';
import { Image } from '../image';
import { FeatureText as FeatureTextComponent } from '../featureText';
import { Button } from '../button';
import { PropsWithClassName } from '../tabselector/PropsWithClassName';

export type ContentCardProps = {
	image?: string;
	imageIcon?: IconName;
	title: string;
	featureTitle?: string;
	subtitle?: string;
	description?: string;
	tall?: boolean;
	clickable?: boolean;
	onClick?: () => void;
	link?: string;
	target?: string;
	disabled?: boolean;
	icon?: IconName;
	tallIcon?: IconName;
	buttonLabel?: string;
	onButtonClick?: () => void;
	logo?: string;
	useBackgroundColor?: boolean;
}

export function ContentCard({
	className,
	image,
	imageIcon,
	title,
	featureTitle,
	subtitle,
	description,
	tall = false,
	clickable = false,
	onClick,
	link,
	target = '_self',
	disabled,
	icon = 'arrow-right',
	tallIcon,
	buttonLabel,
	onButtonClick,
	logo,
	useBackgroundColor = false,
}: PropsWithClassName<ContentCardProps>) {

	const isClickable = clickable || Boolean(link);

	const inner = <>
		{tall && tallIcon && (
			<ContentCardIcon>
				<Icon icon={tallIcon} />
			</ContentCardIcon>
		)}
		{image && (
			<ContentCardImageWrapper $tall={tall}>
				<Image
					src={image}
					alt={title}
					aspectRatio={tall ? '16/9' : '1/1'}
					objectFit="contain"
				/>
			</ContentCardImageWrapper>
		)}
		{!image && imageIcon && (
			<ContentCardImageIcon $tall={tall}>
				<Icon icon={imageIcon} />
			</ContentCardImageIcon>
		)}
		<ContentCardBody $tall={tall}>
			<ContentCardText>
				{tall && logo ? (
					<ContentCardTitleRow $tall={tall}>
						<ContentCardLogo src={logo} alt="" />
						<ContentCardTitle $tall={tall}>
							{title}
						</ContentCardTitle>
					</ContentCardTitleRow>
				) : (
					<ContentCardTitleRow $tall={tall}>
						<>
							{featureTitle && (
								<FeatureTextComponent direction='left' text={title} featureText={featureTitle} />
							)}
							{!featureTitle && (
								<ContentCardTitle $tall={tall}>
									{title}
								</ContentCardTitle>
							)}
						</>
					</ContentCardTitleRow>
				)}
				{subtitle && (
					<ContentCardSubtitle $tall={tall}>
						{subtitle}
					</ContentCardSubtitle>
				)}
				{description && (
					<ContentCardDescription>
						{description}
					</ContentCardDescription>
				)}
				{tall && buttonLabel && (
					<ContentCardButtonRow>
						<Button
							variant={'link'}
							label={buttonLabel}
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
								onButtonClick?.();
							}}
							style={{ padding: 0 }}
						/>
					</ContentCardButtonRow>
				)}
			</ContentCardText>
			{isClickable && (
				<ContentCardAction $icon={icon}>
					<Icon icon={icon} />
				</ContentCardAction>
			)}
		</ContentCardBody>
	</>;

	if (link) {
		return <ContentCardLink
			className={className}
			href={disabled ? undefined : link}
			target={target}
			$tall={tall}
			$disabled={disabled}
			$icon={icon}
			$useBackgroundColor={useBackgroundColor}
		>
			{inner}
		</ContentCardLink>;
	}

	return <ContentCardRoot
		className={className}
		$tall={tall}
		$clickable={clickable}
		$disabled={disabled}
		onClick={clickable ? onClick : undefined}
		$icon={icon}
		$useBackgroundColor={useBackgroundColor}
	>
		{inner}
	</ContentCardRoot>;
}
